import React, { useEffect, useRef } from 'react';
import { ArrowRight, Building2, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import { siteContent } from '../content/siteContent';

const audienceIconMap = {
  rocket: Rocket,
  building2: Building2,
};

const TargetAudience = () => {
  const sectionRef = useRef(null);
  const { targetAudience } = siteContent;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const audienceCards = [targetAudience.startup, targetAudience.corporate];

  return (
    <section ref={sectionRef} className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#D22527] rounded-full filter blur-3xl opacity-10"></div>
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#88171A] rounded-full filter blur-3xl opacity-10"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-2 gap-12">
          {audienceCards.map((audience, index) => {
            const Icon = audienceIconMap[audience.icon];

            return (
              <div
                key={audience.heading}
                className="fade-in-up group p-12 rounded-3xl bg-gradient-to-br from-[#D22527]/10 to-transparent border border-[#D22527]/30 hover:border-[#D22527]/50 transition-all duration-500 backdrop-blur-sm hover:scale-105"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#D22527] to-[#88171A] flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500 group-hover:rotate-6">
                  <Icon className="w-10 h-10 text-white" />
                </div>

                <h3 className="text-4xl font-bold text-white mb-4">{audience.heading}</h3>
                <p className="text-2xl text-[#D22527] font-semibold mb-6">{audience.subheading}</p>
                <p className="text-gray-400 text-lg leading-relaxed mb-8">{audience.intro}</p>

                <div className="space-y-4 mb-8">
                  {audience.bullets.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#D22527]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-[#D22527]"></div>
                      </div>
                      <span className="text-white">{item}</span>
                    </div>
                  ))}
                </div>

                <p className="text-xl text-white font-light mb-8">
                  {audience.closingLead}{' '}
                  <span className="font-bold text-[#D22527]">{audience.closingAccent}</span>
                </p>

                <Button
                  onClick={scrollToContact}
                  className="group bg-gradient-to-r from-[#D22527] to-[#88171A] text-white hover:shadow-lg hover:shadow-[#D22527]/50 transition-all duration-300 hover:scale-105 px-6 py-6 text-lg border-0 w-full"
                >
                  {audience.cta}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TargetAudience;
